import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, queryClient, useAuth } from '@weebee/api';
import { BrandMark } from '@/components/shared/brand-mark';
import { setCompanyStore } from './company-store';

interface CompanyInfo {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | undefined;
  verified: boolean;
}

interface Membership {
  companyId: string;
  companySlug: string;
  companyName: string;
  companyLogoUrl: string | undefined;
  companyVerified: boolean;
  level: number;
}

interface CompanyContextValue {
  company: CompanyInfo;
  level: number;
  memberships: Membership[];
  switchCompany: (slug: string) => void;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}

interface CompanyProviderProps {
  children: ReactNode;
  onNoCompany: () => void;
  onError?: (message: string) => void;
}

export function CompanyProvider({ children, onNoCompany, onError }: CompanyProviderProps) {
  const authState = useAuth();
  const [memberships, setMemberships] = useState<Membership[] | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authState.status !== 'authenticated') return;

    let cancelled = false;

    async function loadMemberships() {
      try {
        const { data, error } = await api.GET('/api/v1/corporate/memberships');
        if (cancelled) return;

        if (error) {
          // API error (auth failure, network, etc.) — not the same as "no memberships"
          const message =
            (error as { detail?: string }).detail ?? 'Failed to load company memberships';
          onError?.(message);
          onNoCompany();
          return;
        }

        if (!data?.memberships?.length) {
          onNoCompany();
          return;
        }

        const mapped: Membership[] = data.memberships.map((m) => ({
          companyId: m.company_id,
          companySlug: m.company_slug,
          companyName: m.company_name,
          companyLogoUrl: m.company_logo_url ?? undefined,
          companyVerified: m.company_verified,
          level: m.level,
        }));

        setMemberships(mapped);

        // Select first company if none selected, or validate current selection
        const saved = localStorage.getItem('weebee-work-company');
        const validSaved = saved && mapped.some((m) => m.companySlug === saved);
        const slug = validSaved ? saved : mapped[0].companySlug;
        setSelectedSlug(slug);

        const selected = mapped.find((m) => m.companySlug === slug);
        if (selected) {
          setCompanyStore(selected.companySlug, selected.level);
        }
      } catch {
        onNoCompany();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadMemberships();
    return () => {
      cancelled = true;
    };
  }, [authState.status, onNoCompany]);

  const switchCompany = useCallback(
    (slug: string) => {
      if (!memberships) return;
      const target = memberships.find((m) => m.companySlug === slug);
      if (!target) return;

      setSelectedSlug(slug);
      setCompanyStore(target.companySlug, target.level);
      localStorage.setItem('weebee-work-company', slug);
      void queryClient.invalidateQueries();
    },
    [memberships]
  );

  if (loading || !memberships || !selectedSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <BrandMark className="h-10 mb-3 animate-pulse" />
          <p className="text-sm text-muted-foreground">Loading your company...</p>
        </div>
      </div>
    );
  }

  const current = memberships.find((m) => m.companySlug === selectedSlug);
  if (!current) return null;

  const value: CompanyContextValue = {
    company: {
      id: current.companyId,
      slug: current.companySlug,
      name: current.companyName,
      logoUrl: current.companyLogoUrl,
      verified: current.companyVerified,
    },
    level: current.level,
    memberships,
    switchCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}
